import { axiosInstance, toastOnError } from '@vl-core/api';
import * as z from 'zod';
import { Options } from './helpers/type-helpers';
import { joinKeys } from './helpers/joinKeys';
import { safeParse } from './helpers/safeParse';

export const path = '/meta/case/APPUSER';

// Helper to coerce null/undefined -> []
const asArray = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((v) => v ?? []);

const UserAuthCancelReasonSchema = z.object({
  auth_cancel_reason_code: z.string(),
  auth_cancel_reason_desc: z.string()
});

const UrgencyChangeReasonSchema = z.object({
  urgency_change_code: z.string(),
  urgency_change_desc: z.string()
});

const CaseDischargeReasonSchema = z.object({
  discharge_code: z.string(),
  discharge_desc: z.string()
});

const CaseCancelReasonSchema = z.object({
  case_cancel_code: z.string(),
  case_cancel_desc: z.string()
});

const EligibilityPolicySchema = z.object({
  policy_name: z.string(),
  policy_code: z.string()
});

const ClinicalUrgencySchema = z.object({
  urg_code: z.string(),
  urg_desc: z.string()
});

const EligibilityCheckStatusCodes = z.object({
  eligibility_check_status_code: z.string(),
  eligibility_check_status_desc: z.string(),
  pending_state: z.number(),
  ineligible_flag: z.number(),
  uninsured_flag: z.number()
});

const CohortSchema = z.object({
  cohort_description: z.string().nullable(),
  cohort_number: z.number(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable()
});

const VirtualOnlyReasonSchema = z.object({
  virtual_only_reason_code: z.string(),
  virtual_only_reason_desc: z.string()
});

const CaseMetaSchema = z.object({
  case_cancel_reasons: asArray(CaseCancelReasonSchema),
  case_discharge_reasons: asArray(CaseDischargeReasonSchema),
  user_auth_cancel_reasons: asArray(UserAuthCancelReasonSchema),
  eligibility_check_status_codes: asArray(EligibilityCheckStatusCodes),
  eligibility_policies: asArray(EligibilityPolicySchema),
  clinical_urgencies: z.array(ClinicalUrgencySchema).nullable(),
  urgency_change_reasons: asArray(UrgencyChangeReasonSchema),
  cohorts: asArray(CohortSchema),
  virtual_only_reason_codes: asArray(VirtualOnlyReasonSchema)
});
export type CaseMeta = {
  case_cancel_reasons: {
    case_cancel_code: string;
    case_cancel_desc: string;
  }[];
  case_discharge_reasons: {
    discharge_code: string;
    discharge_desc: string;
  }[];
  user_auth_cancel_reasons: {
    auth_cancel_reason_code: string;
    auth_cancel_reason_desc: string;
  }[];
  eligibility_check_status_codes: {
    eligibility_check_status_code: string;
    eligibility_check_status_desc: string;
    pending_state: number;
    ineligible_flag: number;
    uninsured_flag: number;
  }[];
  eligibility_policies: {
    policy_name: string;
    policy_code: string;
  }[];
  clinical_urgencies:
    | {
        urg_code: string;
        urg_desc: string;
      }[]
    | null;
  urgency_change_reasons: {
    urgency_change_code: string;
    urgency_change_desc: string;
  }[];
  cohorts: {
    cohort_description: string | null;
    cohort_number: number;
    start_date: string | null;
    end_date: string | null;
  }[];
  virtual_only_reason_codes: {
    virtual_only_reason_code: string;
    virtual_only_reason_desc: string;
  }[];
};

export const ResponseSchema = z.object({
  meta: z.array(CaseMetaSchema)
});
export type Response = {
  meta: CaseMeta[];
};

export const ParamsSchema = z.object({
  case_id: z.number().optional(),
  pat_guid: z.string().optional(),
  speccat: z.string().optional(),
  specarea: z.string().optional()
});
export type Params = {
  case_id?: number;
  pat_guid?: string;
  speccat?: string;
  specarea?: string;
};

export const getMetaCase = async (params?: Params, options?: Options): Promise<CaseMeta[]> =>
  axiosInstance
    .get(path, {
      params: ParamsSchema.parse(params),
      ...options?.axiosConfig
    })
    .then((res) => safeParse(res.data, z.array(CaseMetaSchema))) as any;

export const getMetaCaseQuery = (params?: Params, options?: Options) =>
  ({
    queryKey: joinKeys(path, params),
    queryFn: () => getMetaCase(params),
    select: (data: CaseMeta[]) => data[0],
    throwOnError: options?.skipToast ? false : toastOnError,
    staleTime: Infinity
  }) as const;

// Tests
let rs = {} as z.infer<typeof ResponseSchema>;
let rt = {} as Response;
let ps = {} as z.infer<typeof ParamsSchema>;
let pt = {} as Params;

rs = rt;
rt = rs;
ps = pt;
pt = ps;
